import { Button } from '@affine/component/ui/button';
import { Modal } from '@affine/component/ui/modal';
import { type I18nString, useI18n } from '@affine/i18n';
import { InformationFillDuotoneIcon } from '@blocksuite/icons/rc';

import * as styles from './storage-full-dialog.css';
import { StorageFullScene } from './storage-full-scene';

export interface StorageFullMessage {
  description: I18nString;
  tips: I18nString[];
  confirmText: I18nString;
  cancelText?: I18nString;
}

/**
 * Tells that the workspace storage is full and sync waits for room, in the
 * layout of the welcome card: the scene above the title, the ways out, the
 * actions side by side.
 */
export const StorageFullDialog = ({
  message,
  onConfirm,
  onClose,
}: {
  message: StorageFullMessage;
  onConfirm: () => void;
  onClose: () => void;
}) => {
  const t = useI18n();
  return (
    <Modal
      animation="none"
      closeButtonOptions={{
        'aria-label': t['com.streamlain.streamlain.common.close'](),
        className: styles.closeButton,
        'data-testid': 'storage-full-dialog-close',
      }}
      contentOptions={{ className: styles.content }}
      description={t.t(message.description)}
      descriptionClassName={styles.description}
      headerClassName={styles.header}
      onOpenChange={nextOpen => {
        if (!nextOpen) onClose();
      }}
      open
      title={
        <span className={styles.title}>
          <InformationFillDuotoneIcon className={styles.titleIcon} />
          <span>{t['com.streamlain.payment.sync-paused.title']()}</span>
        </span>
      }
      width={520}
    >
      <StorageFullScene className={styles.scene} />
      <div className={styles.body} data-testid="storage-full-dialog">
        {message.tips.length > 0 ? (
          <ul className={styles.ways}>
            {message.tips.map(tip => (
              <li
                className={styles.way}
                key={typeof tip === 'string' ? tip : tip.i18nKey}
              >
                {t.t(tip)}
              </li>
            ))}
          </ul>
        ) : null}
        <div
          className={styles.actions}
          data-single={message.cancelText ? undefined : ''}
        >
          {message.cancelText ? (
            <Button
              className={styles.action}
              data-testid="storage-full-dialog-cancel"
              onClick={onClose}
              variant="secondary"
            >
              {t.t(message.cancelText)}
            </Button>
          ) : null}
          <Button
            autoFocus
            className={styles.action}
            data-testid="storage-full-dialog-confirm"
            onClick={onConfirm}
            variant="primary"
          >
            {t.t(message.confirmText)}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
